import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { FormField, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ROLES, ACTIVITY_MODULES } from "@/lib/constants/options";
import { useAuthStore } from "@/features/auth";
import { useActivityLogStore } from "../hooks";

const ActivityLogFilters = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const {
    filters,
    users,
    usersLoading,
    usersLoaded,
    loadUsers,
    setFilters,
    resetFilters,
  } = useActivityLogStore();

  const isBusinessUnit = user?.role === ROLES.BUSINESS_UNIT;

  useEffect(() => {
    if (!isBusinessUnit || usersLoading || usersLoaded) return;
    loadUsers();
  }, [isBusinessUnit, loadUsers, usersLoaded, usersLoading]);

  const moduleOptions = useMemo(() => {
    const options = Object.values(ACTIVITY_MODULES).map((module) => ({
      key: module,
      label: t(module),
    }));

    return [{ key: "All", label: t("All") }, ...options];
  }, [t]);

  const userOptions = useMemo(() => {
    const options = users.map((item) => ({
      key: item?._id || item?.id || "",
      label: item?.name || item?.username || item?.email || "",
    }));

    return [{ key: "All", label: t("All") }, ...options].filter(
      (item) => item.key,
    );
  }, [t, users]);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
      {isBusinessUnit ? (
        <SelectField
          label={t("Users")}
          value={filters.selectedUserId}
          onValueChange={(value) => setFilters({ selectedUserId: value })}
          options={userOptions}
          placeholder={t("Select user")}
          disabled={usersLoading}
        />
      ) : null}

      <SelectField
        label={t("Module")}
        value={filters.module}
        onValueChange={(value) => setFilters({ module: value })}
        options={moduleOptions}
        placeholder={t("Select module")}
      />

      <FormField
        label={t("Search")}
        placeholder={t("Search")}
        value={filters.searchText}
        onChange={(event) => setFilters({ searchText: event.target.value })}
      />

      <FormField
        label={t("Start Date")}
        type="date"
        value={filters.startDate}
        onChange={(event) => setFilters({ startDate: event.target.value })}
      />

      <FormField
        label={t("End Date")}
        type="date"
        value={filters.endDate}
        onChange={(event) => setFilters({ endDate: event.target.value })}
      />

      <div className="flex items-end">
        <Button
          type="button"
          variant="ghost"
          onClick={resetFilters}
          className="h-10 px-0 text-sm text-destructive hover:text-destructive/80"
        >
          {t("Clear All Filters")}
        </Button>
      </div>
    </div>
  );
});

export default ActivityLogFilters;
